import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Home,
  MessageCircle,
  FileText,
  Palette,
  Settings,
  Share2,
  BarChart2,
  ChevronDown,
  LogOut,
  User,
  Layers,
  Zap,
  Globe,
  Bot,
  ChevronRight,
  ToggleLeft,
  Menu,
  Clipboard,
  Cpu,
  ArrowLeftFromLine,
  Shield
} from 'lucide-react';
import { useUser } from '../../../context/UserContext';

// Logo Component
const LogoIcon = ({ collapsed }) => (
  <div className={`transition-all duration-300 flex items-center justify-center ${collapsed ? 'w-10 h-10' : 'w-8 h-8'}`}>
    <img src="/favicon.ico" alt="SmushLabs" className="w-full h-full object-contain" />
  </div>
);

// Compact Usage Component for Sidebar
const CompactUsage = () => {
  const [expanded, setExpanded] = useState(false);
  const { usage, planLimits } = useUser();

  const usageItems = [
    {
      label: 'Chatbots',
      type: 'chatbots',
      current: usage.chatbots || 0,
      max: planLimits.chatbots,
      color: '#94a3b8'  // slate-400 - subtle
    },
    {
      label: 'Queries',
      type: 'monthlyQueries',
      current: usage.monthlyQueries || 0,
      max: planLimits.monthlyQueries,
      color: '#94a3b8'
    },
    {
      label: 'Files',
      type: 'fileUploads',
      current: usage.fileUploads || 0,
      max: planLimits.fileUploads,
      color: '#94a3b8'
    }
  ];

  const formatNum = (n) => {
    if (n === -1) return '∞';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n;
  };

  return (
    <div className="border border-slate-200/60 rounded-xl bg-white/50 p-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-1 py-0.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
      >
        <span className="font-medium flex items-center gap-1.5">
          <BarChart2 size={11} className="text-slate-400" />
          Usage
        </span>
        <ChevronDown size={11} className={`text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-2 space-y-2.5 pt-2 border-t border-slate-100">
          {usageItems.map((item) => {
            const isUnlimited = item.max === -1;
            const percentage = isUnlimited ? 0 : Math.min((item.current / item.max) * 100, 100);

            return (
              <div key={item.type} className="space-y-0.5">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="font-medium text-slate-500">
                    {item.label}
                  </span>
                  <span className="tabular-nums text-slate-400">
                    {formatNum(item.current)}/{formatNum(item.max)}
                  </span>
                </div>
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: isUnlimited ? '0%' : `${percentage}%`,
                      backgroundColor: item.color
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

function Sidebar({ activeTab, setActiveTab, selectedChatbotId, activeSubTab, isMobileMenuOpen, onMobileMenuToggle, chatbots, isCollapsed, onToggleCollapse }) {
  const { user, canImpersonate, getPlanDisplayName, signOutUser } = useUser();
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  // State to track expanded submenus in the sidebar (Desktop Expanded Mode)
  const [expandedMenus, setExpandedMenus] = useState({
    'Build': true,
    'Grow': true
  });

  const navigate = useNavigate();
  const [activeFloatingMenu, setActiveFloatingMenu] = useState(null);
  const sidebarRef = useRef(null);
  const switcherRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check for floating menu closing (outside entire sidebar)
      if (sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest('.floating-menu-portal')) {
        setActiveFloatingMenu(null);
      }

      // Check for context switcher closing (outside the switcher area specifically)
      if (switcherRef.current && !switcherRef.current.contains(event.target)) {
        setIsSwitcherOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedChatbot = chatbots?.find(c => c.chatbot_id === selectedChatbotId);

  const handleParentClick = (item, e) => {
    if (isCollapsed) {
      if (item.subItems) {
        // Floating Menu Logic
        if (activeFloatingMenu?.id === item.id) {
          setActiveFloatingMenu(null);
        } else {
          // Calculate top position
          const rect = e.currentTarget.getBoundingClientRect();
          setActiveFloatingMenu({ id: item.id, top: rect.top });
        }
      } else {
        handleNavClick(item.id);
      }
    } else {
      // Normal Expanded Behavior
      if (item.subItems) {
        setExpandedMenus(prev => ({ ...prev, [item.id]: !prev[item.id] }));
      } else {
        handleNavClick(item.id);
      }
    }
  };

  // --- Navigation Config ---
  const chatbotNavStructure = [
    { type: 'header', label: 'Main' },
    { id: 'Inbox', icon: MessageCircle, label: 'Inbox', isPrimary: true },
    { id: 'Dashboard', icon: BarChart2, label: 'Analytics' },
    { id: 'AdminImpersonation', icon: Shield, label: 'Admin Panel', hidden: !canImpersonate },

    { type: 'header', label: 'Build' },
    {
      id: 'Knowledge',
      icon: FileText,
      label: 'Knowledge',
      subItems: [
        { id: 'Website', label: 'Website', icon: Globe },
        { id: 'Files', label: 'Files', icon: FileText },
        { id: 'OpenAI', label: 'OpenAI', icon: Cpu, hidden: !(selectedChatbot?.config?.openAIVectorStoreID || selectedChatbot?.config?.openAIAssistantID) }
      ]
    },
    {
      id: 'Design',
      icon: Palette,
      label: 'Design',
      subItems: [
        { id: 'Identity', label: 'Identity', icon: User },
        { id: 'Theme', label: 'Theme', icon: Palette },
        { id: 'Toggle Button', label: 'Toggle Button', icon: ToggleLeft }
      ]
    },
    {
      id: 'Behavior',
      icon: Settings,
      label: 'Behavior',
      subItems: [
        { id: 'General', label: 'General', icon: Settings },
        { id: 'Chat Menu', label: 'Chat Menu', icon: Menu },
        { id: 'ProactiveBubble', label: 'Proactive', icon: MessageCircle },
        { id: 'HumanHandoff', label: 'Human Handoff', icon: User },
        { id: 'Forms', label: 'Forms', icon: Clipboard },
        { id: 'Walkthroughs', label: 'Walkthroughs', icon: Zap, badge: 'Beta' }
      ]
    },

    { type: 'header', label: 'Grow' },
    {
      id: 'Connect',
      icon: Share2,
      label: 'Connect'
    },
    {
      id: 'Publish',
      icon: Globe,
      label: 'Publish'
    },
  ];

  const platformNavStructure = [
    { type: 'header', label: 'Platform' },
    { id: 'Chatbot', icon: Home, label: 'Overview' },
    { id: 'Inbox', icon: MessageCircle, label: 'Unified Inbox' },
    { id: 'Analytics', icon: BarChart2, label: 'Unified Analytics' },
    { id: 'AdminImpersonation', icon: Shield, label: 'Admin Panel', hidden: !canImpersonate },
  ];

  const navItems = selectedChatbotId ? chatbotNavStructure : platformNavStructure;

  const handleNavClick = (itemId, subItem = null) => {
    if (itemId === 'AdminImpersonation') {
      setActiveTab('AdminImpersonation');
      if (isMobileMenuOpen) onMobileMenuToggle(false);
      setActiveFloatingMenu(null);
      return;
    }

    const targetSubTab = subItem ? subItem.id : (
      // Defaults if clicking parent
      itemId === 'Knowledge' ? 'Files' :
        itemId === 'Design' ? 'Identity' :
          itemId === 'Behavior' ? 'General' :
            itemId === 'Connect' ? 'Integrations' :
              itemId === 'Publish' ? 'Publish' :
                itemId === 'Analytics' ? 'Dashboard' : itemId
    );

    if (selectedChatbotId) {
      if (itemId === 'Inbox') {
        setActiveTab('Inbox');
      } else {
        setActiveTab(`Chatbot/${targetSubTab}`);
      }
    } else {
      setActiveTab(itemId);
    }

    if (isMobileMenuOpen) onMobileMenuToggle(false);
    setActiveFloatingMenu(null);
  };

  const handleChatbotSwitch = (botId) => {
    if (botId === null) {
      if (window.innerWidth < 768) onMobileMenuToggle(false);
      navigate('/overview');
    } else {
      if (window.innerWidth < 768) onMobileMenuToggle(false);
      navigate(`/chatbot/${botId}/design/identity`);
    }
    setIsSwitcherOpen(false);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-20 md:hidden backdrop-blur-sm"
          onClick={() => onMobileMenuToggle(false)}
        />
      )}

      {/* Sidebar Container - Contra Style: Light Gray BG */}
      <div
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 bg-slate-50/80 border-r border-slate-200/60 z-30 transition-all duration-300 ease-in-out flex flex-col backdrop-blur-xl
            ${isMobileMenuOpen ? 'translate-x-0 w-64' : '-translate-x-full md:translate-x-0'}
            ${isCollapsed ? 'md:w-20' : 'md:w-64'}
        `}
      >

        {/* 1. Header */}
        <div className={`flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-6'} py-6 relative transition-all`}>
          <div className="flex items-center gap-3 cursor-pointer overflow-hidden" onClick={() => setActiveTab('Chatbot')}>
            <LogoIcon collapsed={isCollapsed} />
            <span className={`font-bold text-xl text-slate-900 tracking-tight transition-opacity duration-200 ${isCollapsed ? 'opacity-0 w-0 hidden' : 'opacity-100'}`}>
              SmushLabs
            </span>
          </div>

          {/* Collapse Toggle */}
          <button
            onClick={onToggleCollapse}
            className="absolute -right-3 top-7 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-900 shadow-sm hidden md:flex hover:scale-110 transition-all z-40"
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ArrowLeftFromLine size={12} />}
          </button>
        </div>

        {/* Context Switcher - Refined */}
        <div className={`px-4 mb-2 transition-all duration-300 ${isCollapsed ? 'px-3' : ''}`}>
          <div className="relative" ref={switcherRef}>
            <button
              onClick={() => setIsSwitcherOpen(!isSwitcherOpen)}
              className={`flex items-center transition-all border group
                            ${isSwitcherOpen ? 'bg-white border-slate-200 shadow-sm' : 'border-transparent hover:bg-white hover:border-slate-200 hover:shadow-sm'}
                            ${isCollapsed ? 'justify-center w-14 h-14 rounded-2xl mx-auto p-0' : 'w-full justify-between p-2 rounded-xl'}
                        `}
              title={selectedChatbot?.config?.assistantName || "Dashboard"}
            >
              <div className="flex items-center gap-3 overflow-hidden">
                {selectedChatbot ? (
                  <div className={`relative rounded-xl overflow-hidden shadow-sm transition-all ${isCollapsed ? 'w-14 h-14' : 'w-8 h-8'}`}>
                    {selectedChatbot.config?.botIconUrl ? (
                      <img src={selectedChatbot.config.botIconUrl} alt="Bot" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-slate-900 text-white flex items-center justify-center">
                        <Bot size={isCollapsed ? 24 : 16} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center transition-all ${isCollapsed ? 'w-14 h-14' : 'w-8 h-8'}`}>
                    <Layers size={isCollapsed ? 24 : 16} />
                  </div>
                )}
                {!isCollapsed && (
                  <div className="text-left overflow-hidden">
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Context</p>
                    <p className="text-sm font-semibold text-slate-700 truncate">
                      {selectedChatbot ? (selectedChatbot.config?.assistantName || 'Untitled') : 'Dashboard'}
                    </p>
                  </div>
                )}
              </div>
              {!isCollapsed && <ChevronDown size={14} className="text-slate-400 group-hover:text-slate-600" />}
            </button>

            {/* Dropdown */}
            {isSwitcherOpen && (
              <div className={`absolute top-full mt-2 bg-white border border-slate-100 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 w-60 ${isCollapsed ? 'left-16 top-0 origin-top-left' : 'left-0 right-0'}`}>
                <div className="max-h-60 overflow-y-auto p-1">
                  <button
                    onClick={() => handleChatbotSwitch(null)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 mb-1 hover:bg-slate-50 text-slate-600"
                  >
                    <Layers size={14} />
                    All Chatbots
                  </button>
                  <div className="h-px bg-slate-100 my-1"></div>
                  {chatbots?.map(bot => (
                    <button
                      key={bot.chatbot_id}
                      onClick={() => handleChatbotSwitch(bot.chatbot_id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${selectedChatbotId === bot.chatbot_id ? 'bg-slate-50 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      <div className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden border border-slate-200/50">
                        {bot.config?.botIconUrl ? (
                          <img src={bot.config.botIconUrl} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Bot size={12} className={selectedChatbotId === bot.chatbot_id ? 'text-slate-900' : 'text-slate-400'} />
                        )}
                      </div>
                      <span className="truncate">{bot.config?.assistantName || 'Untitled'}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 2. Scrollable Navigation */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-2 space-y-1 custom-scrollbar">
          {navItems.map((item, index) => {
            if (item.type === 'header') {
              if (isCollapsed) return <div key={index} className="h-6"></div>; // Larger spacer in collapsed
              return (
                <div key={index} className="px-6 pt-4 pb-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{item.label}</p>
                </div>
              );
            }

            // Check if active (Parent level)
            const isActiveParent = selectedChatbotId
              ? (item.id === 'Inbox' && activeTab === 'Inbox') || (item.subItems?.some(sub => sub.id === activeSubTab))
              : (activeTab === item.id);

            if (item.hidden) return null;

            return (
              <div key={item.id} className="px-3 md:px-4 relative group">
                <div className="relative">
                  <button
                    onClick={(e) => handleParentClick(item, e)}
                    disabled={item.disabled}
                    className={`w-full flex items-center transition-all duration-200 group/btn
                        ${isCollapsed ? 'justify-center w-10 h-10 mx-auto rounded-xl p-0' : 'justify-between px-3 py-2.5 rounded-xl'}
                        ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        
                        /* Active State Logic - Minimal Text Prominence (No Background) */
                        ${isActiveParent
                        ? 'text-slate-900 font-bold'
                        : 'text-slate-500 font-medium hover:text-slate-900 hover:bg-slate-50'
                      }
                    `}
                    title={isCollapsed ? item.label : ''}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon && <item.icon size={isCollapsed ? 20 : 18} strokeWidth={isActiveParent ? 2 : 2} className={isActiveParent ? 'text-slate-900' : 'text-slate-500 group-hover/btn:text-slate-700'} />}
                      {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                    </div>

                    {/* Chevron for items with children */}
                    {item.subItems && !isCollapsed && (
                      <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 ${expandedMenus[item.id] ? 'rotate-180' : ''}`} />
                    )}
                  </button>
                </div>

                {/* INLINE SUB-MENU (Expanded Mode) */}
                {item.subItems && !isCollapsed && (
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedMenus[item.id] ? 'max-h-64 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
                    <div className="pl-4 ml-3 border-l-[1.5px] border-slate-200 space-y-0.5 py-1">
                      {item.subItems.map(subItem => {
                        if (subItem.hidden) return null;

                        // Precise sub-active check
                        const isSubActive = selectedChatbotId && activeSubTab === subItem.id;

                        return (
                          <button
                            key={subItem.id}
                            onClick={() => handleNavClick(item.id, subItem)}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors
                                                         ${isSubActive ? 'text-slate-900 font-bold' : 'text-slate-500 font-medium hover:text-slate-900 hover:bg-slate-50'}
                                                     `}
                          >
                            <span className="relative">
                              {subItem.label}
                              {subItem.badge && (
                                <span className="absolute left-full -top-1.5 ml-1 text-[9px] bg-blue-50 text-blue-600 px-1 py-0.5 rounded-[4px] font-bold uppercase tracking-wider border border-blue-100 leading-none">
                                  {subItem.badge}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 3. User Footer with Usage */}
        <div className={`border-t border-slate-200 bg-slate-50/50 z-10 transition-all ${isCollapsed ? 'p-2' : 'px-3 py-2'}`}>
          {/* Compact Usage Section - Expandable */}
          {!isCollapsed && (
            <CompactUsage />
          )}

          {/* User Info - Compact */}
          <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all cursor-pointer ${isCollapsed ? 'justify-center w-10 h-10 p-0 mx-auto' : 'mt-2 border border-slate-200/60 bg-white/50 hover:bg-white hover:shadow-sm'}`}>
            <div className={`rounded-full bg-white flex items-center justify-center text-slate-600 font-bold border border-slate-200 shadow-sm ${isCollapsed ? 'w-9 h-9 text-sm' : 'w-7 h-7 text-xs'}`}>
              {user?.displayName?.[0] || 'U'}
            </div>
            {!isCollapsed && (
              <div className="flex-1 overflow-hidden min-w-0">
                <p className="text-xs font-semibold text-slate-700 truncate">{user?.displayName || 'User'}</p>
                <p className="text-[9px] text-slate-400 font-medium">
                  {getPlanDisplayName()}
                </p>
              </div>
            )}
            {!isCollapsed && (
              <button onClick={signOutUser} className="text-slate-400 hover:text-red-500 transition-colors p-1 hover:bg-red-50 rounded-md">
                <LogOut size={14} />
              </button>
            )}
          </div>
        </div>

        {/* PORTAL FLOATING MENU */}
        {isCollapsed && activeFloatingMenu?.id && createPortal(
          <div
            className="floating-menu-portal fixed z-[9999] bg-white rounded-2xl shadow-[0_4px_24px_-4px_rgba(0,0,0,0.12)] p-2 animate-in fade-in slide-in-from-left-2 duration-200 w-64 border border-slate-100/50"
            style={{
              top: Math.max(10, activeFloatingMenu.top - 10),
              left: '5.5rem' // 88px
            }}
          >
            {(() => {
              const item = navItems.find(i => i.id === activeFloatingMenu.id);
              if (!item || !item.subItems) return null;
              return (
                <div className="flex flex-col">
                  <div className="px-4 py-3 pb-2">
                    <p className="text-sm font-medium text-slate-500">{item.label}</p>
                  </div>
                  <div className="space-y-1 px-1 pb-1">
                    {item.subItems.map(subItem => {
                      if (subItem.hidden) return null;
                      const isSubActive = selectedChatbotId && activeSubTab === subItem.id;
                      return (
                        <button
                          key={subItem.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavClick(item.id, subItem);
                          }}
                          className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-all duration-200 flex items-center justify-between group
                                            ${isSubActive ? 'text-slate-900 font-bold' : 'text-slate-600 font-medium hover:bg-slate-50 hover:text-slate-900'}
                                        `}
                        >
                          <div className="flex items-center gap-2">
                            <span>{subItem.label}</span>
                            {subItem.badge && (
                              <span className="text-[9px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border border-blue-100">
                                {subItem.badge}
                              </span>
                            )}
                          </div>
                          {isSubActive && <div className="w-1.5 h-1.5 rounded-full bg-slate-900" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>,
          document.body
        )}
      </div>
    </>
  );
}

export default Sidebar;
